import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  ArrowRight, Copy, ChevronRight, CheckCircle, Circle,
  UserPlus, Mail, Building2, ShoppingCart, Code2, Package,
  RefreshCw, Bot, Shield, Settings, MessageSquare, Link2,
  Terminal, Target, BarChart3, Brain, AlertTriangle, Trophy
} from 'lucide-react';
import { toast } from 'sonner';

export default function DocsOnboardingChecklist() {
  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Copied to clipboard');
  };

  const [completed, setCompleted] = useState({});

  const toggleItem = (id) => {
    setCompleted(prev => {
      const next = { ...prev, [id]: !prev[id] };
      const allItems = categories.flatMap(cat => cat.items);
      const newCount = allItems.filter(item => next[item.id]).length;
      if (!prev[id]) {
        if (newCount === allItems.length) {
          toast.success('All steps completed! You are fully onboarded.');
        } else {
          toast.success('Step completed!');
        }
      }
      return next;
    });
  };

  const categories = [
    {
      title: 'Account Setup',
      icon: UserPlus,
      color: '#3EF1C7',
      items: [
        { id: 'create-account', label: 'Create your Commerce Index account', desc: 'Sign up via Magic Link or Google OAuth at commerceindex.com/signup.' },
        { id: 'verify-email', label: 'Verify your email address', desc: 'Click the verification link sent to your email to activate your account.' },
        { id: 'business-info', label: 'Set your business information', desc: 'Add business name, industry, currency, and timezone in Settings > Business Profile.' },
      ],
    },
    {
      title: 'Platform Connection',
      icon: Link2,
      color: '#7A5CFF',
      items: [
        { id: 'connect-platform', label: 'Connect Shopify OR set up API access', desc: 'Install from Shopify App Store, or generate an API key in Settings > Developer.' },
        { id: 'import-products', label: 'Import your product catalog', desc: 'Auto-synced for Shopify. For others, upload CSV or use the REST API.' },
        { id: 'verify-sync', label: 'Verify product sync is working', desc: 'Check the Products tab to confirm all items imported correctly with pricing and inventory.' },
      ],
    },
    {
      title: 'Agent Configuration',
      icon: Bot,
      color: '#FFD166',
      items: [
        { id: 'enable-agents', label: 'Enable at least 3 AI agents', desc: 'We recommend: Pricing Agent, Inventory Agent, and Deal Agent to start.' },
        { id: 'review-guardrails', label: 'Review default guardrails', desc: 'Check minimum margins, max price changes, and approval thresholds for each agent.' },
        { id: 'set-approval-prefs', label: 'Set approval preferences', desc: 'Choose which agent actions need your approval vs. run autonomously.' },
      ],
    },
    {
      title: 'Messaging Setup',
      icon: MessageSquare,
      color: '#FF6B8A',
      items: [
        { id: 'connect-messaging', label: 'Connect Telegram, Slack, or WhatsApp', desc: 'Go to Settings > Messaging and follow the platform-specific setup guide.' },
        { id: 'link-account', label: 'Link your account via OTP', desc: 'Send /start to the bot, then enter the one-time code shown in your dashboard.' },
        { id: 'test-pulse', label: 'Test the /pulse command', desc: 'Send /pulse in your connected messaging app to see your store health summary.' },
      ],
    },
    {
      title: 'Goals & Monitoring',
      icon: Target,
      color: '#38BDF8',
      items: [
        { id: 'set-revenue-goal', label: 'Set your first revenue goal', desc: 'Use /goal set or the Goals tab in the dashboard to define a monthly revenue target.' },
        { id: 'configure-briefs', label: 'Configure daily briefs', desc: 'Choose what time and which platform to receive your morning commerce brief.' },
        { id: 'review-decisions', label: 'Review your first agent decisions', desc: 'Check the Decision Graph to see how agents are analyzing your data and what they recommend.' },
      ],
    },
  ];

  const allItems = categories.flatMap(cat => cat.items);
  const totalItems = allItems.length;
  const completedCount = allItems.filter(item => completed[item.id]).length;
  const progressPercent = totalItems > 0 ? Math.round((completedCount / totalItems) * 100) : 0;

  return (
    <div className="max-w-4xl">
      {/* Hero */}
      <div className="mb-12">
        <Badge className="bg-[#3EF1C7]/20 text-[#3EF1C7] border-[#3EF1C7]/30 mb-4">
          Onboarding
        </Badge>
        <h1 className="text-4xl font-bold text-white mb-4" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
          Onboarding Checklist
        </h1>
        <p className="text-lg text-[#94A3B8]">
          Track your progress through Commerce Index setup. Click each item as you complete it.
          This checklist is local to your browser and not persisted to the server.
        </p>
      </div>

      {/* Progress Bar */}
      <section className="mb-12">
        <Card className="bg-[#0D1628] border-[#22304A]">
          <CardContent className="p-6">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-3">
                <Trophy className="w-5 h-5 text-[#FFD166]" />
                <h2 className="text-lg font-semibold text-white" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
                  Overall Progress
                </h2>
              </div>
              <span className="text-[#3EF1C7] font-mono text-sm font-semibold">
                {completedCount} / {totalItems} ({progressPercent}%)
              </span>
            </div>
            <div className="w-full h-3 bg-[#1E293B] rounded-full overflow-hidden border border-[#334155]">
              <div
                className="h-full rounded-full transition-all duration-500 ease-out"
                style={{
                  width: `${progressPercent}%`,
                  backgroundColor: '#3EF1C7',
                  opacity: completedCount === 0 ? 0 : 1,
                }}
              />
            </div>
            {completedCount === totalItems && (
              <div className="mt-3 p-3 bg-[#3EF1C7]/10 border border-[#3EF1C7]/30 rounded-lg">
                <p className="text-[#3EF1C7] text-sm font-medium flex items-center gap-2">
                  <CheckCircle className="w-4 h-4" />
                  All done! Your Commerce Index setup is complete. Your agents are now observing and learning.
                </p>
              </div>
            )}
            {completedCount > 0 && completedCount < totalItems && (
              <p className="text-[#64748B] text-sm mt-3">
                {totalItems - completedCount} step{totalItems - completedCount !== 1 ? 's' : ''} remaining.
                Keep going — each step makes your agents smarter.
              </p>
            )}
            {completedCount === 0 && (
              <p className="text-[#64748B] text-sm mt-3">
                Click each item to check it off as you complete it. Progress is tracked locally in this browser session.
              </p>
            )}
          </CardContent>
        </Card>
      </section>

      {/* Checklist Categories */}
      {categories.map((category, catIndex) => {
        const CatIcon = category.icon;
        const catCompleted = category.items.filter(item => completed[item.id]).length;
        const catTotal = category.items.length;

        return (
          <section key={catIndex} className="mb-12">
            <div className="flex items-center gap-3 mb-4">
              <div
                className="w-10 h-10 rounded-lg flex items-center justify-center"
                style={{ backgroundColor: `${category.color}20` }}
              >
                <CatIcon className="w-5 h-5" style={{ color: category.color }} />
              </div>
              <div>
                <h2 className="text-2xl font-semibold text-white" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
                  {category.title}
                </h2>
                <span className="text-xs text-[#64748B] font-mono">
                  {catCompleted} / {catTotal} complete
                </span>
              </div>
            </div>

            <div className="space-y-3">
              {category.items.map((item) => {
                const isCompleted = completed[item.id];
                return (
                  <div
                    key={item.id}
                    onClick={() => toggleItem(item.id)}
                    className={`flex items-start gap-4 p-4 rounded-lg border cursor-pointer transition-all ${
                      isCompleted
                        ? 'bg-[#3EF1C7]/5 border-[#3EF1C7]/30'
                        : 'bg-[#0D1628] border-[#22304A] hover:border-[#334155]'
                    }`}
                  >
                    <div className="flex-shrink-0 mt-0.5">
                      {isCompleted ? (
                        <CheckCircle className="w-5 h-5 text-[#3EF1C7]" />
                      ) : (
                        <Circle className="w-5 h-5 text-[#334155]" />
                      )}
                    </div>
                    <div>
                      <h3 className={`font-medium ${isCompleted ? 'text-[#3EF1C7] line-through' : 'text-white'}`}>
                        {item.label}
                      </h3>
                      <p className="text-sm text-[#94A3B8] mt-0.5">{item.desc}</p>
                    </div>
                  </div>
                );
              })}
            </div>
          </section>
        );
      })}

      {/* Note about persistence */}
      <section className="mb-12">
        <div className="p-4 bg-[#1E293B] border border-[#334155] rounded-lg">
          <div className="flex items-center gap-2 mb-2">
            <AlertTriangle className="w-4 h-4 text-[#FFD166]" />
            <span className="text-[#FFD166] text-sm font-medium">Note</span>
          </div>
          <p className="text-sm text-[#94A3B8]">
            This checklist state is stored locally in your browser. It is not persisted to the server
            and will reset if you clear your browser data or switch devices.
          </p>
        </div>
      </section>

      {/* What's Next */}
      <section>
        <h2 className="text-2xl font-semibold text-white mb-6" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
          What's Next?
        </h2>
        <Card className="bg-[#0D1628] border-[#22304A]">
          <CardContent className="p-6">
            <ul className="space-y-3">
              {[
                { text: 'Explore all 80+ AI agents', link: '/docs/agents' },
                { text: 'OpenClaw Command Reference', link: '/docs/commands' },
                { text: 'Commerce Health Score explained', link: '/docs/ci-score' },
                { text: 'REST API Reference', link: '/docs/rest-api' },
                { text: 'Trust Ladder configuration', link: '/docs/trust-ladder' },
              ].map((item, i) => (
                <li key={i}>
                  <Link to={item.link} className="flex items-center gap-2 text-[#3EF1C7] hover:underline">
                    <ChevronRight className="w-4 h-4" />
                    {item.text}
                  </Link>
                </li>
              ))}
            </ul>
          </CardContent>
        </Card>
      </section>
    </div>
  );
}
